import './MultiSelect.scss'
import { useId, useMemo, useRef, useState, type ComponentProps, type ReactNode } from 'react'
import { MdOutlineClose, MdOutlineExpandMore } from 'react-icons/md'
import { clx } from '../clx'
import { IconButton } from '../IconButton'
import {
  assignRef,
  buildModel,
  defaultFilter,
  ListboxPopup,
  optionId,
  useListbox,
  type ListboxFilter,
  type ListboxOption,
  type ListboxOptionState
} from '../Listbox'

export type MultiSelectOption = ListboxOption
export type MultiSelectOptionState = ListboxOptionState

export interface MultiSelectProps extends Omit<
  ComponentProps<'input'>,
  'value' | 'defaultValue' | 'onChange' | 'size' | 'type' | 'children'
> {
  options: readonly MultiSelectOption[]
  value: readonly string[]
  onChange: (value: string[]) => void
  /** Match an option against the typed query. Default: case-insensitive substring of `textValue ?? label`. */
  filter?: ListboxFilter
  /** Custom option content. Rows must stay one line high. */
  renderOption?: (option: MultiSelectOption, state: MultiSelectOptionState) => ReactNode
  /** Show a button that removes every value. */
  isClearable?: boolean
  isLoading?: boolean
  emptyText?: ReactNode
  isDisabled?: boolean
  isReadOnly?: boolean
  size?: 'sm' | 'md'
}

/**
 * Multi-value combobox: the same listbox and keyboard model as Combobox,
 * with `aria-multiselectable`. Picking toggles and keeps the popup open;
 * values show as removable chips and Backspace in an empty input removes
 * the last one.
 */
export function MultiSelect({
  options,
  value,
  onChange,
  filter = defaultFilter,
  renderOption,
  isClearable = false,
  isLoading = false,
  emptyText = 'No results',
  isDisabled = false,
  isReadOnly = false,
  size = 'md',
  disabled,
  readOnly,
  placeholder,
  className,
  ref,
  onKeyDown,
  onBlur,
  onClick,
  ...rest
}: MultiSelectProps) {
  const listId = `multi-select-${useId()}-listbox`
  const inputRef = useRef<HTMLInputElement | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState('')

  const model = useMemo(() => buildModel(options, query, filter), [options, query, filter])
  const byValue = useMemo(() => new Map(options.map((o) => [o.value, o])), [options])
  const selected = useMemo(() => new Set(value), [value])
  const isInteractive = !(isDisabled || disabled || isReadOnly || readOnly)

  const remove = (v: string) => {
    onChange(value.filter((x) => x !== v))
  }

  const listbox = useListbox({
    model,
    selectedIndex: -1,
    isAutoActive: query !== '',
    isMulti: true,
    onPick: (option) => {
      if (selected.has(option.value)) remove(option.value)
      else onChange([...value, option.value])
    }
  })

  const dismiss = () => {
    listbox.close()
    setQuery('')
  }

  return (
    <div
      ref={anchorRef}
      className={clx(
        'multi-select',
        size !== 'md' && `multi-select--is-${size}`,
        value.length > 0 && 'multi-select--has-value',
        className
      )}
      data-disabled={isDisabled || disabled || undefined}
      onMouseDown={(event) => {
        // A click on the control's padding focuses the input.
        if (event.target !== event.currentTarget || !isInteractive) return
        event.preventDefault()
        inputRef.current?.focus()
        if (!listbox.isOpen) listbox.open()
      }}
    >
      {value.length > 0 && (
        <ul className="multi-select__chips" aria-label="Selected">
          {value.map((v) => {
            const label = byValue.get(v)?.label ?? v
            return (
              <li key={v} className="multi-select__chip">
                <span className="multi-select__chip-label">{label}</span>
                {isInteractive && (
                  <button
                    type="button"
                    className="multi-select__chip-remove"
                    aria-label={`Remove ${label}`}
                    onClick={() => {
                      remove(v)
                      inputRef.current?.focus()
                    }}
                  >
                    <MdOutlineClose aria-hidden="true" />
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
      <input
        {...rest}
        ref={(node) => {
          inputRef.current = node
          assignRef(ref, node)
        }}
        type="text"
        role="combobox"
        className="multi-select__input"
        autoComplete="off"
        spellCheck={false}
        disabled={isDisabled || disabled}
        readOnly={isReadOnly || readOnly}
        placeholder={value.length === 0 ? placeholder : undefined}
        aria-autocomplete="list"
        aria-expanded={listbox.isOpen}
        aria-controls={listbox.isOpen ? listId : undefined}
        aria-activedescendant={
          listbox.isOpen && listbox.activeIndex >= 0
            ? optionId(listId, listbox.activeIndex)
            : undefined
        }
        value={query}
        onChange={(event) => {
          if (!isInteractive) return
          setQuery(event.target.value)
          if (!listbox.isOpen) listbox.open()
          listbox.resetActive()
        }}
        onClick={(event) => {
          onClick?.(event)
          if (isInteractive && !listbox.isOpen) listbox.open()
        }}
        onKeyDown={(event) => {
          onKeyDown?.(event)
          if (event.defaultPrevented || !isInteractive) return
          if (event.key === 'Escape') {
            // Open: the Escape stack closes the popup. Closed: drop the typed text.
            if (!listbox.isOpen && query !== '') {
              event.preventDefault()
              setQuery('')
            }
            return
          }
          if (event.key === 'Backspace' && query === '' && value.length > 0) {
            event.preventDefault()
            onChange(value.slice(0, -1))
            return
          }
          if (event.key === 'Tab') setQuery('')
          listbox.onKeyDown(event)
        }}
        onBlur={(event) => {
          onBlur?.(event)
          if (anchorRef.current?.contains(event.relatedTarget)) return
          dismiss()
        }}
      />
      {isClearable && value.length > 0 && isInteractive && (
        <IconButton
          label="Clear"
          icon={<MdOutlineClose />}
          size="sm"
          hasTooltip={false}
          className="multi-select__clear"
          onClick={() => {
            onChange([])
            inputRef.current?.focus()
          }}
        />
      )}
      <span
        className="multi-select__chevron"
        aria-hidden="true"
        onMouseDown={(event) => {
          event.preventDefault()
          if (!isInteractive) return
          inputRef.current?.focus()
          if (listbox.isOpen) listbox.close()
          else listbox.open()
        }}
      >
        <MdOutlineExpandMore />
      </span>
      {listbox.isOpen && (
        <ListboxPopup
          id={listId}
          anchorRef={anchorRef}
          inputRef={inputRef}
          model={model}
          activeIndex={listbox.activeIndex}
          isSelected={(option) => selected.has(option.value)}
          onPick={listbox.pick}
          onActiveChange={listbox.setActiveIndex}
          onDismiss={dismiss}
          renderOption={renderOption}
          isMulti
          isLoading={isLoading}
          emptyText={emptyText}
          size={size}
        />
      )}
    </div>
  )
}
